import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'

import 'react-loading-skeleton/dist/skeleton.css'
import * as SvgIcons from 'assets/svg/sprite'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { usePreviewContext } from 'context/previewContext'
import PreviewComments from 'features/Preview/PreviewComments/PreviewComments'
import SinglePostPreview from 'features/Preview/SinglePostPreview/SinglePostPreview'
import { getPaginatedPost, useDeletePost } from 'helpers/hooks'
import { enqueuePostView } from 'helpers/trackingViews'
import {
  type PostLiveStatsInclude,
  getPostLiveStats
} from 'services/endpoints/post'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'
import { type IPost } from 'types/interfaces/ITypes'

import styles from './_postModal.module.scss'

const hasOwn = (value: unknown, property: string): boolean =>
  value !== null &&
  typeof value === 'object' &&
  Object.prototype.hasOwnProperty.call(value, property)

const hasPostMetrics = (post: unknown): boolean =>
  hasOwn(post, 'views') &&
  hasOwn(post, 'unique_views') &&
  hasOwn(post, 'impressions_raw') &&
  hasOwn(post, 'unique_impressions')

const hasPostMonetization = (post: unknown): boolean =>
  hasOwn(post, 'overall_revenue') &&
  hasOwn(post, 'purchases_count') &&
  hasOwn(post, 'purchases_sum') &&
  hasOwn(post, 'tips_count') &&
  hasOwn(post, 'tips_sum')

const PostModal: FC<{
  postId: string
  post?: any
  closeFn: () => void
  newPostPage?: boolean
  commentId?: string
}> = ({ postId, post, closeFn, newPostPage, commentId }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const userData = useUserProfile()
  const modalContext = useModalContext()

  const [hasControls, setHasControls] = useState(true)
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)

  const { setInitialData, setSelectedPostData, setCommentsActive } =
    usePreviewContext()

  const selectedPostData = usePreviewStore(s => s.selectedPostData)
  const commentsActive = usePreviewStore(s => s.commentsActive)
  const { deletePostMutation } = useDeletePost({
    onMutate: async () => undefined,
    onError: () => {}
  })
  const queryKey = useMemo(
    () => ['paginatedSinglePost', postId] as const,
    [postId]
  )

  const isMissingFromPayload =
    post === null ||
    post === 'deleted' ||
    Boolean(post?.missing)

  const shouldQueryPost = !!postId && post === undefined

  const removeSearchQuery = useCallback(() => {
    navigate({ search: undefined })
  }, [navigate])

  const openOptions = useCallback(() => {
    setIsOptionsNavOpen(true)
  }, [])

  useEffect(() => {
    setHasControls(true)
    setIsOptionsNavOpen(false)
    setCommentsActive(Boolean(commentId) && !isMissingFromPayload)
  }, [postId, commentId, isMissingFromPayload, setCommentsActive])

  const initialQueryData = useMemo(() => {
    if (!post?.id || isMissingFromPayload) return undefined

    return {
      pages: [
        {
          page: {
            data: {
              data: [post]
            }
          }
        }
      ],
      pageParams: [undefined]
    }
  }, [post, isMissingFromPayload])

  const { data, isFetching, isError, isFetched } = useQuery(
    queryKey,
    async () => await getPaginatedPost(postId),
    {
      refetchOnWindowFocus: false,
      enabled: shouldQueryPost,
      keepPreviousData: false,
      retry: false,
      initialData: initialQueryData
    }
  )

  const queriedPost = useMemo<IPost | undefined>(
    () => data?.pages?.[0]?.page?.data?.data?.[0],
    [data]
  )

  const postData = useMemo<IPost | undefined>(() => {
    if (isMissingFromPayload) return undefined
    return queriedPost ?? post
  }, [isMissingFromPayload, queriedPost, post])

  const missingStatsIncludes = useMemo<PostLiveStatsInclude[]>(() => {
    if (!postData?.id) return []

    const include: PostLiveStatsInclude[] = []

    if (!hasPostMetrics(postData)) include.push('metrics')
    if (!hasPostMonetization(postData)) include.push('monetization')

    return include
  }, [postData])

  const statsIncludeKey = missingStatsIncludes.join(',')

  const isOwnPost =
    (postData?.user_id ?? postData?.user?.id) === userData?.id

  const { data: liveStats } = useQuery(
    ['postLiveStats', postId, statsIncludeKey],
    async () => await getPostLiveStats(postId, missingStatsIncludes),
    {
      enabled:
        !isMissingFromPayload &&
        Boolean(postData?.id) &&
        isOwnPost &&
        missingStatsIncludes.length > 0,
      refetchOnWindowFocus: false,
      retry: false
    }
  )

  const displayPost = useMemo<IPost | undefined>(() => {
    if (isMissingFromPayload) return undefined

    const currentPost =
      selectedPostData?.id === postId
        ? selectedPostData
        : postData

    if (!currentPost) return undefined

    return {
      ...currentPost,
      ...(liveStats ?? {})
    }
  }, [
    isMissingFromPayload,
    selectedPostData,
    postData,
    liveStats,
    postId
  ])

  const postMissing = useMemo(() => {
    if (isMissingFromPayload) return true
    if (displayPost?.id) return false

    return shouldQueryPost && (isError || (isFetched && !isFetching))
  }, [
    isMissingFromPayload,
    displayPost?.id,
    shouldQueryPost,
    isError,
    isFetched,
    isFetching
  ])

  const missingPostShell = useMemo(() => {
    if (!postMissing) return undefined

    return {
      id: postId,
      user_id: post?.user_id ?? post?.user?.id ?? '',
      user: post?.user ?? {
        id: post?.user_id ?? '',
        username: '',
        display_name: '',
        avatar: { url: null },
        cropped_avatar: { url: null }
      },
      body: null,
      photos: [],
      videos: [],
      sounds: [],
      photos_preview: [],
      videos_preview: [],
      price: null,
      is_purchased: true,
      isLiked: false,
      is_engage: false,
      likes_count: 0,
      comment_count: 0,
      missing: true
    } as any
  }, [postMissing, postId, post])

  const previewPost = postMissing ? missingPostShell : displayPost

  const hasPostMedia =
    (previewPost?.photos?.length ?? 0) > 0 ||
    (previewPost?.videos?.length ?? 0) > 0 ||
    (previewPost?.sounds?.length ?? 0) > 0

  const viewerOptions = useMemo(() => {
    if (!previewPost?.id || postMissing) return []

    return [
      {
        icon: <SvgIcons.IconOptionsPlaylist />,
        text: t('addToList'),
        disabled: !hasPostMedia,
        action: () => {
          setIsOptionsNavOpen(false)

          modalContext.addModal(
            '',
            <AddToPlaylist
              itemId={post.id}
              itemType='post'
              closeFn={() => modalContext.clearModal()}
            />,
            true,
            true
          )
        }
      }
    ]
  }, [
    previewPost?.id,
    postMissing,
    hasPostMedia,
    postId,
    t,
    modalContext
  ])

  useEffect(() => {
    if (
      postMissing ||
      !postData?.id ||
      postData.id !== postId ||
      !userData?.id
    ) {
      return
    }

    const creatorId = postData.user_id ?? postData.user?.id

    if (!creatorId || creatorId === userData.id) return

    const timer = window.setTimeout(() => {
      enqueuePostView(postId)
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [
    postMissing,
    postData?.id,
    postData?.user_id,
    postData?.user?.id,
    userData?.id,
    postId
  ])

  useEffect(() => {
    const viewerId = userData?.id

    if (
      postMissing ||
      !viewerId ||
      !postData?.id ||
      postData.id !== postId
    ) {
      return
    }

    setInitialData(viewerId, 'all', '', undefined, undefined, {
      selectedPost: postId,
      selectedSlide: 0,
      query: queryKey as any
    })

    setSelectedPostData(postData as any)
  }, [
    postMissing,
    userData?.id,
    postId,
    postData,
    postData?.id,
    setInitialData,
    setSelectedPostData,
    queryKey
  ])
const role = useMemo(() => {
  return userData && userData?.id === previewPost?.user?.id
    ? 'owner'
    : ''
}, [userData, previewPost?.user?.id])
  return (
    <div className={styles.postContainer}>
      {previewPost?.id && (
        <>
          {!postMissing && isOptionsNavOpen && (
            <OptionsDropdown
              role={role}
              content="post"
              isOptionsNavOpen={isOptionsNavOpen}
              setIsOptionsNavOpen={setIsOptionsNavOpen}
              postId={postId}
              options={viewerOptions}
              deleteFn={() => {
                if (!previewPost?.id || postMissing) return

                deletePostMutation.mutate(postId, {
                  onSuccess: () => {
                    setIsOptionsNavOpen(false)
                    modalContext.clearModal()
                    closeFn()
                  }
                })
              }}
              withoutBottomNavigation
            />
          )}

          <SinglePostPreview
            post={previewPost}
            showControls={hasControls}
            isFirstPost={false}
            hasNextPage={false}
            isInViewport
            minimizeFn={() => {}}
            toggleControls={() => setHasControls(v => !v)}
            newPostPage={newPostPage}
            disablePreviewContextSync
            onClose={closeFn}
            setIsOptionsNavOpen={setIsOptionsNavOpen}
            onOpenOptions={openOptions}
          />

          {commentsActive && !postMissing && (
            <PreviewComments
              commentsActive={commentsActive}
              setCommentsActive={setCommentsActive}
              activeCommentsPostId={postId}
              commentCount={previewPost.comment_count ?? 0}
              isMyPost={
                Number(userData?.id) ===
                Number(previewPost.user?.id)
              }
              isSingleCommentLink={false}
              viewAllCommentsFn={removeSearchQuery}
            />
          )}
        </>
      )}
    </div>
  )
}

export default PostModal